import { type NoyauCode, noyauCodeSchema } from '@rosetta/shared';
import { z } from 'zod';
import type { ConfigTahiti } from '../config';
import { CODE_LANGUE, idSchema, lireLignes, texteLocaliseSchema } from './h2data';
import type { AppelHttp } from './http';

/**
 * The calls that read the result of a finished task: the table of the noyaux, to turn a
 * `rose_core_id` into a code, then the `rose_document_translation` rows of the document.
 */

export const ligneNoyauSchema = z.object({
  id: idSchema,
  code: z.string().min(1),
});

/** `rose_core_id` of Tahiti -> code of the noyau. */
export type CorrespondanceNoyaux = ReadonlyMap<string, NoyauCode>;

/**
 * Reads the noyaux of Tahiti (`rose_core_dictionary`: one row per noyau, its `code`
 * being `SGP`, `SPG`, ...). The identifiers differ from one environment to the next,
 * which is why they are read and never written in the code. Rows whose code is not one
 * of the six are ignored.
 *
 * The caller keeps the result: the table does not change during a session.
 */
export async function lireCorrespondanceNoyaux(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
): Promise<CorrespondanceNoyaux> {
  const lignes = await lireLignes(
    appel,
    config,
    jeton,
    { table: 'rose_core_dictionary', fields: ['id', 'code'], limit: 100 },
    ligneNoyauSchema,
  );

  const correspondance = new Map<string, NoyauCode>();
  for (const ligne of lignes) {
    const code = noyauCodeSchema.safeParse(ligne.code.trim().toUpperCase());
    if (code.success) correspondance.set(ligne.id, code.data);
  }
  return correspondance;
}

export const ligneResultatSchema = z.object({
  id: idSchema,
  rose_core_id: idSchema.nullish(),
  assembled_text: texteLocaliseSchema.nullish(),
  /** The translation sub-task (`rose_task_v5_traduction`) that wrote the row (#11). */
  h2_task_id: idSchema.nullish(),
});

/** What a finished task produced for one noyau. */
export interface ResultatLu {
  noyau: NoyauCode;
  texte: string;
  /** The translation sub-task that wrote it, when Tahiti says (#11: where its state is read). */
  sousTacheId?: string;
}

/**
 * Reads the translation of each noyau of a document, as the reference script does: the
 * `assembled_text` of its `rose_document_translation` rows, filtered by
 * `rose_document_id`. When a noyau has several rows, the most recent one (the highest
 * identifier) is the translation; if its text is empty the noyau has no result. A row for a
 * noyau Tahiti does not know is left out: the caller reads a missing noyau as a failure.
 *
 * A document can carry the translations of several runs (#11: a text translated towards another
 * noyau later is a new run on the same document): the most recent row of each noyau is the one
 * read, whichever run wrote it.
 *
 * TO VALIDATE(#11): that a row is written only when the translation of its noyau has ended well
 * (seen on devdb, 2026-10-08: the four cancelled translations of 910633 and the failed GSP of
 * 908461 have none, the five finished ones of 908461 have theirs, linked by `h2_task_id`).
 */
export async function lireResultats(
  appel: AppelHttp,
  config: ConfigTahiti,
  jeton: string,
  documentId: string,
  noyaux: CorrespondanceNoyaux,
): Promise<ResultatLu[]> {
  const lignes = await lireLignes(
    appel,
    config,
    jeton,
    {
      table: 'rose_document_translation',
      fields: ['id', 'rose_core_id', 'assembled_text', 'h2_task_id'],
      where: ['rose_document_id', Number(documentId), '='],
      limit: 100,
      lang: CODE_LANGUE,
    },
    ligneResultatSchema,
  );

  const recents = new Map<NoyauCode, (typeof lignes)[number]>();
  for (const ligne of lignes) {
    const noyau = ligne.rose_core_id == null ? undefined : noyaux.get(ligne.rose_core_id);
    if (noyau === undefined) continue;
    const precedent = recents.get(noyau);
    if (precedent === undefined || Number(ligne.id) > Number(precedent.id)) {
      recents.set(noyau, ligne);
    }
  }

  const lus: ResultatLu[] = [];
  for (const [noyau, ligne] of recents) {
    const texte = ligne.assembled_text;
    if (texte != null && texte.trim() !== '') {
      lus.push({
        noyau,
        texte,
        ...(ligne.h2_task_id == null ? {} : { sousTacheId: ligne.h2_task_id }),
      });
    }
  }
  return lus;
}
